import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { each } from 'lodash';
import validator from 'validator';
import { takeUntil } from 'rxjs';

import { BaseComponent, ErrorsUtil, MessageBoxComponent, WaitComponent } from '@sircon/sircon-ui-framework';

import { AuthenticationService } from 'src/services/authentication.service';
import { AuthenticationHttpService } from 'src/providers/authentication-http.service';
import { NgIf } from '@angular/common';

/**
 * Authenticate Controller
 * URL: /#/authenticate
 * Description: Used to manage authentication into the system
 *   from external mechanisms
 */
@Component({
    selector: 'app-authenticate',
    templateUrl: './authenticate.component.html',
    styleUrls: ['./authenticate.component.less'],
    imports: [NgIf, MessageBoxComponent, WaitComponent]
})
export class AuthenticateComponent extends BaseComponent implements OnInit {

    token;
    loading: boolean;
    errors;
    redirectRoute;
    redirectParam;
    priorUserId: string;

    constructor(
        private window: Window,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private authenticationService: AuthenticationService,
        private authenticationHttpService: AuthenticationHttpService
    ) {
        super();
    }

    ngOnInit() {
        this.loading = true;
        this.errors = ErrorsUtil.create();

        // Capture the CURRENTLY logged-in user's id (decoded from the PLM-USER-AUTH cookie) BEFORE any
        // auth work runs. Both auth branches below call authenticationHttpService.logout() first, which
        // CLEARS that cookie - so this must be read here, not in redirectFromRoute() where it would already
        // be gone. redirectFromRoute() compares it against the new user's id to decide whether a genuine
        // user SWITCH happened in this tab (see the reload note there). Undefined on a cold external login.
        this.priorUserId = this.authenticationService.getUserId();

        // Get parameters from Angular Router
        this.activatedRoute.queryParamMap.pipe(takeUntil(this.ngUnsubscribe)).subscribe(params => {
            this.token = params.get('token');
            this.redirectRoute = params.get('redirectRoute');

            // redirectParam may arrive either as repeated keys (?redirectParam=a=1&redirectParam=b=2,
            // as sent by external/PM links) or as a single comma-separated value (as some in-app
            // callers send it). getAll() captures every occurrence; splitting each on comma covers both.
            const redirectParamValues = params.getAll('redirectParam');
            this.redirectParam = redirectParamValues.length
                ? redirectParamValues.flatMap(value => value.split(','))
                : undefined;

            if (this.token) {
                if (validator.isJWT(this.token)) {
                    this.generateCookieFromToken(this.token);
                } else {
                    // Do not attempt to create cookie with an invalid token
                    this.handleErrors();
                }
            } else {
                this.translateSession();
            }
        });
    }

    /**
     * Invokes API to get a JWT for the user, then authenticate into the system with the returned token.
     * This implementation is currently geared towards reporting users based on the pages that are being routed to.
     */
    translateSession() {
        this.authenticationHttpService.logout();
        this.authenticationHttpService.getTokenForSsoId().toPromise()
            .then((res: any) => {
                this.authenticationService.authenticateByToken(res.token)
                    .then(() => {
                        this.redirectFromRoute();
                    })
                    .catch(() => {
                        this.handleErrors();
                    });
            })
            .catch(() => {
                this.handleErrors();
            });
    }

    /**
     * If a token is already provided, populate the cookies and proceed with routing.
     * This implementation is currently geared towards securities users based on the pages that are being routed to.
     *
     * @param token - JWT
     */
    generateCookieFromToken(token) {
        this.authenticationHttpService.logout();
        this.authenticationService.authenticateByToken(token)
            .then(() => {
                this.redirectFromRoute();
            })
            .catch(() => {
                this.handleErrors();
            });
    }

    handleErrors() {
        this.errors.unexpectedError = true;
        this.loading = false;
    }

    redirectFromRoute() {
        sessionStorage.clear();

        // Resolve the final destination (path + params) once, so both the normal in-app navigation and the
        // user-switch reload path below target the same place.
        let targetPath: string;
        const targetParams: any = {};
        if (this.redirectRoute) {
            if (this.redirectParam) {
                each(this.redirectParam, (param) => {
                    const values = param.split('=');
                    targetParams[values[0]] = values[1];
                });
            }
            targetParams.fromRoute = 'dashboard.activity';
            // Convert UI-Router route names to Angular Router paths
            targetPath = this.convertUiRouterToAngularRoute(this.redirectRoute);
        } else {
            targetPath = '/dashboard';
        }

        // When a DIFFERENT user has replaced an active session in this same tab (the classic case: clicking
        // a doc-share / SSO deep link while already logged in as another user), we must fully reload so the
        // persistent unified header (CSS display, not *ngIf - see app.component.html) re-initializes. It
        // bootstraps the external shui micro-frontend exactly once per page load and derives its branding
        // (account name/logo, waffle app-switcher vs. producer banner) plus user-scoped data on that one-shot
        // init; the sui-user-context event patches display values but never re-derives the branding chrome or
        // re-fetches alerts, so without a reload the header keeps the PREVIOUS user's identity. This mirrors
        // the same-tab-switch reload LoginComponent already does for /login. FOF-7831 tracks refreshing the
        // header in place so both reload paths can eventually be removed.
        //
        // We gate on a genuine user CHANGE (prior id != new id) rather than merely "a session existed", so a
        // same-user re-entry (e.g. re-following one's own link) does NOT trigger a jarring extra reload. A
        // cold external login has no priorUserId, so it never reloads.
        const newUserId = this.authenticationService.getUserId();
        const isUserSwitch = this.priorUserId && newUserId && this.priorUserId !== newUserId;

        if (isUserSwitch) {
            // Hard-reload straight to the target. We build the destination as an Angular UrlTree and drop it
            // into location.hash (the app uses hash routing) so query params like networkId/producerId
            // survive the reload, then force a full page load. We deliberately do NOT router.navigate() first:
            // an in-app navigation would paint the destination under the stale previous-user header for a
            // moment before the reload tears it down. See LoginComponent.reloadToRoute() for the same pattern.
            const urlTree = this.router.createUrlTree([targetPath], { queryParams: targetParams });
            this.reloadToRoute(this.router.serializeUrl(urlTree));
        } else if (this.redirectRoute) {
            this.router.navigate([targetPath], {
                queryParams: targetParams,
                replaceUrl: true  // Replace /authenticate in history so back button works correctly
            });
        } else {
            this.router.navigate([targetPath], {
                replaceUrl: true
            });
        }
    }

    /**
     * Points the (hash-routed) URL at the given serialized app route and forces a full page reload, so the
     * fresh boot lands directly on that route with a re-initialized header. Used for the user-switch case in
     * redirectFromRoute(). Split out (mirrors LoginComponent.reloadToRoute) so tests can spy on it and avoid
     * an actual browser reload.
     */
    reloadToRoute(serializedUrl: string): void {
        this.window.location.hash = serializedUrl;
        this.window.location.reload();
    }

    /**
     * Convert UI-Router state names to Angular Router paths
     *
     * This mapping handles deep links from external systems (like Producer Manager)
     * that still use UI-Router state names. Add new mappings as needed when external
     * systems link to routes that have been renamed during Angular Router migration.
     */
    private convertUiRouterToAngularRoute(uiRouterState: string): string {
        const routeMapping: { [key: string]: string } = {
            // Dashboard routes
            'dashboard': '/dashboard',
            'dashboard.activity': '/dashboard/activity',
            'dashboard.allActions': '/dashboard/allActions',

            // Network routes
            'network.agencyProfile.activity': '/network/agencyProfile/activity',
            'network.entity': '/network/entity',
            'network.entity.activity': '/network/entity/activity',

            // Account routes
            'accountProfile': '/accountProfile',

            // Workflow routes - nested states that share the same path
            'workflow': '/workflow',
            'workflow.interview': '/workflow',
            'interview': '/workflow',

            // Reporting routes
            'reporting': '/reporting',
            'reporting-interactive': '/reporting/interactive',
            'reporting-standard': '/reporting/standard',
            'reporting-pentaho': '/reporting/pentaho'
        };

        // Check for explicit mapping first
        if (routeMapping[uiRouterState]) {
            return routeMapping[uiRouterState];
        }

        // Default: convert dot notation to slash notation
        // 'network.entity.activity' → '/network/entity/activity'
        return '/' + uiRouterState.replace(/\./g, '/');
    }
}
